<template>
  <BaseSidebar
    v-model="modelValue"
    title="Solicitar excepcionalidade"
    :description="
      currentStep === 0
        ? `As excepcionalidades são exceções às regras padrão de plantões, que acontecem por acordos individuais entre escalistas e médicos ou por necessidades operacionais imprevistas. Preencha esse formulário e envie um pedido para o seu gestor caso seja necessária uma excepcionalidade nessa vaga`
        : ''
    "
    :icon="mdiLockOpenVariant"
  >
    <template v-if="stationContext" #contextHeader>
      <StationContextCard v-bind="stationContext" :default-expanded="false" />
    </template>

    <!-- Trava do Pós-Plantão (RN-07 / RF-12). Defesa em profundidade: quem abre
         esta sidebar já tem o botão desabilitado, mas a varredura pode aprovar
         com o wizard aberto. Banner no topo, e não só no submit, para o usuário
         não preencher os 4 passos antes de descobrir. -->
    <div
      v-if="isPostStationLocked"
      class="flex items-start gap-2 px-3 py-2 mb-6 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary text-left"
      data-testid="new-exceptionality-post-station-lock-banner"
    >
      <SVGIcon :path="mdiLock" class="w-4 h-4 mt-0.5 shrink-0 text-primary30" />
      <span>{{ postStationLockMessage ?? POST_STATION_LOCK_BANNER_TEXT }}</span>
    </div>

    <div v-if="currentStep !== 0" class="flex items-center w-full mb-10">
      <template v-for="(label, index) in stepLabels" :key="index">
        <div
          class="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold"
          :class="currentStep - 1 >= index ? 'bg-primary30' : 'bg-gray-300'"
        ></div>

        <div
          v-if="index < stepLabels.length - 1"
          class="flex-grow h-[2px] mx-2"
          :class="currentStep - 1 > index ? 'bg-primary30' : 'bg-gray-300'"
        ></div>
      </template>
    </div>

    <div>
      <div v-if="currentStepKey === 'select_attributes'">
        <CheckboxSelect
          v-model="formState.selectedAttributes"
          title="Seleção dos atributos"
          description="Escolha um ou mais atributos excepcionais necessários para a atualização do plantão"
          :options="attributeOptions"
        />
        <p v-if="isForwardButtonDisabled" class="text-sm text-red-500 text-left mt-2">
          Selecione pelo menos um atributo para poder avançar.
        </p>
      </div>

      <div v-if="currentStepKey === 'repass_mode'" class="rounded-lg">
        <div class="flex flex-col gap-4">
          <h1 class="text-left font-bold text-lg text-textPrimary mb-2">Modalidade de repasse</h1>
          <SelectableCard
            v-for="opcao in transferOptions"
            :key="opcao.value"
            :title="opcao.title"
            :description="opcao.description"
            :selected="formState.paymentTiming === opcao.value"
            @click="formState.paymentTiming = opcao.value"
          />
        </div>
      </div>

      <div v-if="currentStepKey === 'proportionality_rule'" class="rounded-lg">
        <div class="flex flex-col gap-4">
          <h1 class="text-left font-bold text-lg text-textPrimary mb-2">
            Regras de proporcionalidade
          </h1>
          <SelectableCard
            v-for="opcao in rulesofProportionality"
            :key="opcao.value"
            :title="opcao.title"
            :description="opcao.description"
            :selected="formState.proportionalityRule === opcao.value"
            @click="formState.proportionalityRule = opcao.value"
          />
        </div>
      </div>

      <div v-if="currentStepKey === 'exceptional_workday'">
        <h1 class="text-left font-bold text-lg text-textPrimary mb-2">Jornada de trabalho</h1>
        <ExceptionalJourney
          v-model="formState.workJourney"
          :original-start="props.originalStart"
          :original-end="props.originalEnd"
          :base-hourly-rate="props.baseHourlyRate"
          :timezone="props.timezone"
        />
      </div>
      <div v-if="currentStepKey === 'agreed_value'">
        <h1 class="text-left font-bold text-lg text-textPrimary mb-2">Valor acordado</h1>
        <AgreedValue
          :base-duration="baseDurationInHours"
          :exceptional-duration="exceptionalDurationInHours"
          :base-value="props.baseHourlyRate"
          :payment-modality="paymentModalityForCard"
          :override-financials="agreedValueOverrideFinancials"
          :is-override-loading="flags.exceptionSimulateMode === 'on' && simulation.loading.value"
          :has-override-error="flags.exceptionSimulateMode === 'on' && !!simulation.error.value"
          @update:agreedValue="formState.agreedValue = $event"
        />
      </div>

      <div v-if="currentStepKey === 'justification'">
        <div class="bg-white p-4 rounded-lg mt-4 text-left text-textPrimary">
          <h2 class="text-lg font-bold">Justificativa de excepcionalidade</h2>

          <p class="text-md mb-4">
            Esse texto irá acompanhar seu pedido de excepcionalidade para que seu supervisor saiba o
            motivo de mudança das regras da vaga de plantão
          </p>

          <TextareaInput
            v-model="formState.justification"
            placeholder="Digite sua justificativa..."
          />
        </div>
      </div>

      <div v-if="currentStepKey === 'summary'">
        <h1 class="text-left font-bold text-lg text-textPrimary mb-2">Resumo do pedido</h1>
        <ExceptionAgreedValueCard
          class="mt-4"
          :original-hourly-value="props.baseHourlyRate"
          :exceptional-cost="displayedExceptionalCost"
          :base-cost="displayedBaseCost"
          :estimated-loss-total="displayedPredictedResult"
          :total-exceptional-value="displayedTotalExceptionalValue"
          :base-duration="baseDurationInHours"
          :exceptional-duration="exceptionalDurationInHours"
        />

        <ExceptionSimpleCard
          class="mt-4"
          title="Modalidade de repasse"
          :description="summaryPaymentTimingLabel"
        />

        <ExceptionSimpleCard
          class="mt-4"
          title="Regras de proporcionalidade"
          :description="summaryProportionalityRuleLabel"
        />

        <ExceptionWorkingDayCard
          class="mt-4"
          :start-time="summaryWorkJourney.startTime"
          :end-time="summaryWorkJourney.endTime"
          :lost-hours="summaryWorkJourney.lostHours"
          :estimated-loss="predictedResult"
        />

        <ExceptionSimpleCard
          class="mt-4"
          title="Justificativa"
          :description="formState.justification"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-around gap-8">
        <OutlinedButton
          :text="currentStep === 0 ? 'Cancelar' : 'Voltar'"
          width="46%"
          class="mt-6 !bg-white"
          @click="currentStep === 0 ? handleCancel() : prevStep()"
        />

        <FilledButton
          :text="forwardButtonText"
          width="46%"
          class="!bg-primary30 mt-6"
          data-testid="new-exceptionality-submit-button"
          @click="handleForward"
          :loading="isSubmitting"
          :disabled="isForwardButtonDisabled || isPostStationLocked"
        />
      </div>
    </template>
  </BaseSidebar>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { mdiLock, mdiLockOpenVariant } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import { POST_STATION_LOCK_BANNER_TEXT } from '@/modules/company/PostStation/constants'
import {
  getPostStationApprovedMessage,
  isPostStationApprovedError
} from '@/modules/company/PostStation/errors'

import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import CheckboxSelect from '@/components/designSystem/Select/CheckboxSelect.vue'
import SelectableCard from '@/components/designSystem/Cards/SelectableCard.vue'
import TextareaInput from '@/components/designSystem/Textarea/TextareaInput.vue'
import ExceptionalJourney from '@/components/designSystem/Cards/ExceptionalJourney.vue'
import AgreedValue from '@/components/designSystem/Cards/AgreedValue.vue'
import ExceptionSimpleCard from '@/components/designSystem/Cards/ExceptionSimpleCard.vue'
import ExceptionWorkingDayCard from '@/components/designSystem/Cards/ExceptionWorkingDayCard.vue'
import ExceptionAgreedValueCard from '@/components/designSystem/Cards/ExceptionAgreedValueCard.vue'
import StationContextCard from '@/components/designSystem/Cards/StationContextCard.vue'
import { attributeOptions, rulesofProportionality, transferOptions } from '@/utils/exceptionality'
import { differenceInMilliseconds, differenceInMinutes } from 'date-fns'
import ExceptionService from '@/Network/Services/DoctorStationException'
import { useToast } from 'vue-toastification'
import { getApiErrorMessage } from '@/utils/apiError'
import { calculeteBaseCost, calculateExceptionalCost, calculatePredictedResult } from '@/utils/math'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useExceptionSimulation, type LocalCalculationInput } from '@/hooks/useExceptionSimulation'
import type { SimulateExceptionPayload } from '@/Network/Types/Requests/DoctorStationExceptionRequests'

interface StationContextData {
  doctorName: string
  doctorCrm?: string
  scheduleName: string
  positionName: string
  shiftName?: string
  team?: string
  timeRange: string
  dutyType?: string
  value?: string
  formation?: string
  repassMode?: string
  proportionalityRule?: string
  breadcrumb?: string
  allocatedBy?: string
  startDetail?: string
  endDetail?: string
  timezone?: string
}

const props = withDefaults(
  defineProps<{
    isOpen: boolean
    originalStart: string
    originalEnd: string
    timezone: string
    baseHourlyRate: number
    proportionalityRule: string
    doctorStationId: number
    originalInCash: boolean
    scheduleId: number | null
    stationContext?: StationContextData
    /** Trava do Pós-Plantão do plantão de origem — ver `ExceptionalitiesSidebar`. */
    postStationLocked?: boolean
  }>(),
  { postStationLocked: false }
)

/**
 * Trava efetiva: a prop de quem abriu OU o 400 do backend, que pode chegar se a
 * varredura aprovar o plantão com o wizard já aberto.
 */
const lockedByServer = ref(false)
const postStationLockMessage = ref<string | null>(null)
const isPostStationLocked = computed(() => props.postStationLocked || lockedByServer.value)

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
  (e: 'requestCreated'): void
}>()

const modelValue = computed({
  get: () => props.isOpen,
  set: (value) => {
    if (!value) {
      resetForm()
    }
    emit('update:isOpen', value)
  }
})

const toast = useToast()
const exceptionService = new ExceptionService()
const isSubmitting = ref(false)
const currentStep = ref(0)

const { flags } = useFeatureFlags()
const simulation = useExceptionSimulation()

const backendToUiProportionalityRuleMap: { [key: string]: string } = {
  INTEGRAL: 'integral',
  PROPORTIONAL_WITH_LIMIT: 'proporcional_com_limite',
  PROPORTIONAL_WITHOUT_LIMIT: 'proporcional_sem_limite'
}

const AGREED_VALUE_HOURLY = 'Valor por hora'

const paymentTimingMap: { [key: string]: 'IMMEDIATE' | 'FLOW' } = {
  a_vista: 'IMMEDIATE',
  fluxo: 'FLOW'
}

const proportionalityRuleMap: {
  [key: string]: 'INTEGRAL' | 'PROPORTIONAL_WITH_LIMIT' | 'PROPORTIONAL_WITHOUT_LIMIT'
} = {
  integral: 'INTEGRAL',
  proporcional_com_limite: 'PROPORTIONAL_WITH_LIMIT',
  proporcional_sem_limite: 'PROPORTIONAL_WITHOUT_LIMIT'
}

const getInitialFormState = () => ({
  selectedAttributes: [],
  paymentTiming: props.originalInCash ? 'a_vista' : 'fluxo',
  proportionalityRule: backendToUiProportionalityRuleMap[props.proportionalityRule] || 'integral',
  workJourney: { start: props.originalStart, end: props.originalEnd },
  agreedValue: {
    type: AGREED_VALUE_HOURLY,
    value: props.baseHourlyRate,
    results: { total: 0, baseCost: 0, exceptionalCost: 0, result: 0 }
  },
  justification: ''
})

const formState = reactive(getInitialFormState())

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      resetForm()
    }
  }
)

const isForwardButtonDisabled = computed(() => {
  if (currentStep.value === 0) {
    return formState.selectedAttributes.length === 0
  }
  return !simulation.canSubmit.value
})

const baseDurationInHours = computed(() => {
  if (!props.originalStart || !props.originalEnd) return 0
  return (
    differenceInMilliseconds(new Date(props.originalEnd), new Date(props.originalStart)) / 3600000
  )
})

const exceptionalDurationInHours = computed(() => {
  const useExceptional = formState.selectedAttributes.includes('exceptional_workday' as never)
  const start = useExceptional ? formState.workJourney.start : props.originalStart
  const end = useExceptional ? formState.workJourney.end : props.originalEnd

  if (!start || !end) return 0
  return differenceInMilliseconds(new Date(end), new Date(start)) / 3600000
})

const paymentModalityForCard = computed(() => {
  const map: { [key: string]: string } = {
    a_vista: 'à vista',
    fluxo: 'no fluxo'
  }
  return map[formState.paymentTiming] || 'no fluxo'
})

const dynamicSteps = computed(() => {
  const optionalSteps = attributeOptions
    .filter((opt) => formState.selectedAttributes.includes(opt.value as never))
    .map((opt) => opt.value)

  return ['select_attributes', ...optionalSteps, 'justification', 'summary']
})

const currentStepKey = computed(() => dynamicSteps.value[currentStep.value])
const totalSteps = computed(() => dynamicSteps.value.length)

const stepLabels = computed(() => {
  const allLabelsMap: { [key: string]: string } = {
    ...Object.fromEntries(attributeOptions.map((opt) => [opt.value, opt.label])),
    justification: 'Justificativa de excepcionalidade',
    summary: 'Resumo do pedido'
  }
  return dynamicSteps.value.slice(1).map((key) => allLabelsMap[key] || '')
})

const summaryPaymentTimingLabel = computed(() => {
  const value = formState.selectedAttributes.includes('repass_mode' as never)
    ? formState.paymentTiming
    : props.originalInCash
      ? 'a_vista'
      : 'fluxo'
  return transferOptions.find((opt) => opt.value === value)?.title || 'N/D'
})

const summaryProportionalityRuleLabel = computed(() => {
  const value = formState.selectedAttributes.includes('proportionality_rule' as never)
    ? formState.proportionalityRule
    : backendToUiProportionalityRuleMap[props.proportionalityRule] || 'integral'
  return rulesofProportionality.find((opt) => opt.value === value)?.title || 'N/D'
})

const summaryWorkJourney = computed(() => {
  const useExceptional = formState.selectedAttributes.includes('exceptional_workday' as never)
  const start = useExceptional ? formState.workJourney.start : props.originalStart
  const end = useExceptional ? formState.workJourney.end : props.originalEnd

  if (!start || !end || !props.originalStart || !props.originalEnd) {
    return { startTime: new Date(), endTime: new Date(), lostHours: '0h00m', estimatedLoss: 0 }
  }

  const exceptionalStart = new Date(start)
  const exceptionalEnd = new Date(end)
  const baseDurationInMinutes = differenceInMinutes(
    new Date(props.originalEnd),
    new Date(props.originalStart)
  )
  const exceptionalDurationInMinutes = differenceInMinutes(exceptionalEnd, exceptionalStart)
  const lostMinutes = baseDurationInMinutes - exceptionalDurationInMinutes

  const hours = Math.floor(Math.abs(lostMinutes) / 60)
  const minutes = Math.abs(lostMinutes) % 60
  const lostHoursString =
    lostMinutes > 0
      ? `${String(hours).padStart(2, '0')}h${String(minutes).padStart(2, '0')}m`
      : '00h00m'

  const estimatedLossValue = lostMinutes > 0 ? (lostMinutes / 60) * props.baseHourlyRate : 0

  return {
    startTime: exceptionalStart,
    endTime: exceptionalEnd,
    lostHours: lostHoursString,
    estimatedLoss: estimatedLossValue
  }
})

const nextStep = () => {
  if (currentStep.value < totalSteps.value - 1) {
    currentStep.value++
  }
}

const prevStep = () => {
  if (currentStep.value > 0) currentStep.value--
}

const handleCancel = () => {
  modelValue.value = false
}

const forwardButtonText = computed(() => {
  return currentStep.value === totalSteps.value - 1 ? 'Finalizar' : 'Avançar'
})

const handleForward = () => {
  if (currentStepKey.value === 'justification' && formState.justification.trim().length < 10) {
    toast.error('A justificativa deve ter no mínimo 10 caracteres.')
    return
  }
  if (currentStep.value === totalSteps.value - 1) {
    submitForm()
  } else {
    nextStep()
  }
}

// eslint-disable-next-line complexity, sonarjs/cognitive-complexity
async function submitForm() {
  if (!props.doctorStationId) {
    console.error('ID do plantão não fornecido.')
    return
  }

  const payload: any = { justification: formState.justification }

  if (formState.selectedAttributes.includes('repass_mode' as never)) {
    payload.paymentTiming = paymentTimingMap[formState.paymentTiming]
  }
  if (formState.selectedAttributes.includes('proportionality_rule' as never)) {
    payload.proportionalityRule = proportionalityRuleMap[formState.proportionalityRule]
  }
  if (formState.selectedAttributes.includes('exceptional_workday' as never)) {
    payload.workJourney = {
      startHour: new Date(formState.workJourney.start).toISOString(),
      endHour: new Date(formState.workJourney.end).toISOString()
    }
  }
  if (formState.selectedAttributes.includes('agreed_value' as never)) {
    if (formState.agreedValue.type === AGREED_VALUE_HOURLY) {
      payload.exceptionalHourlyValue = Number(formState.agreedValue.value).toFixed(2)
    } else {
      payload.exceptionalFullJourneyValue = Number(formState.agreedValue.value).toFixed(2)
    }
  }

  if (props.scheduleId) {
    payload.scheduleId = props.scheduleId
  }

  isSubmitting.value = true
  try {
    await exceptionService.createForDoctorStation(props.doctorStationId, payload)
    toast.success(`Solicitação registrada com sucesso. ID #${props.doctorStationId}`)
    emit('requestCreated')
    handleCancel()
  } catch (error: any) {
    // Trava do Pós-Plantão: além do toast, deixa o banner e o disable de pé para
    // o usuário não tentar de novo às cegas. O wizard permanece aberto e o
    // formulário preenchido (`handleCancel` só roda no sucesso).
    if (isPostStationApprovedError(error)) {
      lockedByServer.value = true
      postStationLockMessage.value = getPostStationApprovedMessage(error)
      toast.error(postStationLockMessage.value)
      return
    }

    // getApiErrorMessage cobre o ServiceError do service (que embrulha o erro
    // HTTP) e o shape axios cru — a extração manual de response.data.message
    // deixaria de funcionar com o erro normalizado.
    toast.error(getApiErrorMessage(error, 'Falha ao criar excepcionalidade.'))
  } finally {
    isSubmitting.value = false
  }
}

function resetForm() {
  currentStep.value = 0
  Object.assign(formState, getInitialFormState())
}

const exceptionalHourlyRate = computed(() => {
  if (!formState.selectedAttributes.includes('agreed_value' as never)) {
    return props.baseHourlyRate
  }
  const agreed = formState.agreedValue
  if (agreed.type === AGREED_VALUE_HOURLY) {
    return Number(agreed.value) || 0
  }
  if (agreed.type === 'Valor por plantão' && exceptionalDurationInHours.value > 0) {
    return (Number(agreed.value) || 0) / exceptionalDurationInHours.value
  }
  return 0
})

const baseCost = computed(() => {
  return calculeteBaseCost(
    baseDurationInHours.value,
    exceptionalDurationInHours.value,
    props.baseHourlyRate
  )
})

const exceptionalPaymentTax = computed(() => {
  if (paymentModalityForCard.value === 'à vista') {
    const feeFromEnv = process.env.VUE_APP_EXCEPTIONAL_REQUEST_FEE_PAYMENT_ADVANCE
    const fee = Number(feeFromEnv)
    return isNaN(fee) ? 0.1 : fee
  }
  return 0
})

const exceptionalCost = computed(() => {
  return calculateExceptionalCost(
    exceptionalDurationInHours.value,
    baseDurationInHours.value,
    exceptionalHourlyRate.value,
    exceptionalPaymentTax.value
  )
})

const predictedResult = computed(() => {
  return calculatePredictedResult(
    exceptionalCost.value,
    baseCost.value,
    exceptionalDurationInHours.value
  )
})

// Espelha `formatFinancials` do backend (ds-billing): valor fechado prevalece como
// informado, sem a volta por valor/hora; nos demais casos, valor/hora × duração
// excepcional. Ambos arredondados ao real, com empate para cima (ROUND_HALF_CEIL).
const totalExceptionalValue = computed(() => {
  const agreed = formState.agreedValue
  if (
    formState.selectedAttributes.includes('agreed_value' as never) &&
    agreed.type !== AGREED_VALUE_HOURLY
  ) {
    return Math.round(Number(agreed.value) || 0)
  }
  // Centavos × minutos em inteiros: o produto em float erra no empate
  // (128,20 × 7,5 = 961,4999...) e cairia um real abaixo do backend.
  const cents = Math.round(exceptionalHourlyRate.value * 100)
  const minutes = Math.round(exceptionalDurationInHours.value * 60)
  return Math.floor((cents * minutes + 3000) / 6000)
})

const simulationPayload = computed<SimulateExceptionPayload>(() => {
  const payload: SimulateExceptionPayload = {}

  if (formState.selectedAttributes.includes('repass_mode' as never)) {
    payload.paymentTiming = paymentTimingMap[formState.paymentTiming]
  }
  if (formState.selectedAttributes.includes('proportionality_rule' as never)) {
    payload.proportionalityRule = proportionalityRuleMap[formState.proportionalityRule]
  }
  if (formState.selectedAttributes.includes('exceptional_workday' as never)) {
    payload.workJourney = {
      startHour: new Date(formState.workJourney.start).toISOString(),
      endHour: new Date(formState.workJourney.end).toISOString()
    }
  }
  if (formState.selectedAttributes.includes('agreed_value' as never)) {
    if (formState.agreedValue.type === AGREED_VALUE_HOURLY) {
      payload.exceptionalHourlyValue = Number(formState.agreedValue.value).toFixed(2)
    } else {
      payload.exceptionalFullJourneyValue = Number(formState.agreedValue.value).toFixed(2)
    }
  }
  if (props.scheduleId) {
    payload.scheduleId = props.scheduleId
  }

  return payload
})

const simulationLocalInput = computed<LocalCalculationInput>(() => ({
  baseDuration: baseDurationInHours.value,
  exceptionalDuration: exceptionalDurationInHours.value,
  baseValue: props.baseHourlyRate,
  agreedHourlyRate: exceptionalHourlyRate.value,
  tax: exceptionalPaymentTax.value
}))

watch(
  [simulationPayload, simulationLocalInput],
  () => {
    simulation.simulate(props.doctorStationId, simulationPayload.value, simulationLocalInput.value)
  },
  { immediate: true, deep: true }
)

watch(currentStepKey, (key) => {
  if (key === 'summary') {
    simulation.simulate(props.doctorStationId, simulationPayload.value, simulationLocalInput.value)
  }
})

const displayedBaseCost = computed(() => {
  if (flags.exceptionSimulateMode !== 'on') return baseCost.value
  const value = simulation.data.value?.baseCost
  return value !== undefined && value !== null ? Number(value) : null
})

const displayedExceptionalCost = computed(() => {
  if (flags.exceptionSimulateMode !== 'on') return exceptionalCost.value
  const value = simulation.data.value?.exceptionalCost
  return value !== undefined && value !== null ? Number(value) : null
})

const displayedPredictedResult = computed(() => {
  if (flags.exceptionSimulateMode !== 'on') return predictedResult.value
  const value = simulation.data.value?.estimatedLoss
  return value !== undefined && value !== null ? Number(value) : null
})

const displayedTotalExceptionalValue = computed(() => {
  if (flags.exceptionSimulateMode !== 'on') return totalExceptionalValue.value
  const value = simulation.data.value?.totalExceptionalValue
  return value !== undefined && value !== null ? Number(value) : null
})

const agreedValueOverrideFinancials = computed(() => {
  return flags.exceptionSimulateMode === 'on' ? simulation.data.value : undefined
})
</script>
